/**
 * upload/validation.ts — the Details-stage validation rules, as pure functions.
 *
 * Kept out of the components on purpose (task requirement: parsing, form state, draft persistence,
 * validation and rendering must be separable). Nothing here touches React or the DOM, so the rules
 * can be reasoned about and tested independently of how a field happens to be rendered.
 *
 * THE BOUNDS ARE NOT INVENTED HERE. Every limit mirrors an existing authority so the client cannot
 * disagree with the server or the database:
 *   name                3–80    app.tool CHECK (0004) + uploadBody zod
 *   description         10–400  app.tool CHECK (0004) + uploadBody zod
 *   problem_solved      10–500  app.tool CHECK (0004) + uploadBody zod
 *   estimated_time      1–600   app.tool CHECK (0004) + uploadBody zod  (UP-F17)
 *   dependencies        ≥1      app.tool NOT NULL (0004)               (UP-F16)
 *   tags                ≤5      CAT-F12 tool_tag_cap trigger (0004)
 *   requirements        ≥10     app.tool connector_setup_notes CHECK (0019)
 * The server remains the authority — this exists so a builder is told what is wrong BEFORE a round
 * trip, and so focus can be moved to the offending field (UP-F44, and the task's "do not rely purely
 * on disabling Next with no explanation").
 *
 * The per-COMPONENT gate is deliberately NOT here: it lives in @trailblazer/shared
 * (missingExplanations/allExplained) because the backend enforces the identical rule and the two must
 * not drift.
 */
import type { DraftDetails } from './draftStore';

/** Field bounds, mirroring app.tool's CHECK constraints and routes/tools.ts's uploadBody. */
export const LIMITS = {
  nameMin: 3,
  nameMax: 80,
  descriptionMin: 10,
  descriptionMax: 400,
  problemMin: 10,
  problemMax: 500,
  timeSavedMin: 1,
  timeSavedMax: 600,
  requirementsMin: 10,
  requirementsMax: 2000,
  maxTags: 5,
  maxTagLength: 60,
} as const;

/** The Details fields that can carry a validation message. Used as focus targets too. */
export type DetailsField =
  | 'name'
  | 'description'
  | 'problemSolved'
  | 'categoryId'
  | 'accessId'
  | 'departmentId'
  | 'estimatedTimeSaved'
  | 'dependencies'
  | 'requirements';

/**
 * Field-keyed validation messages. Absent key = valid. Ordered iteration is provided by
 * `DETAILS_FIELD_ORDER` so "focus the first incomplete field" means first ON THE PAGE, not first by
 * object key order.
 */
export type DetailsErrors = Partial<Record<DetailsField, string>>;

/** Page order of the Details fields — the order the builder reads them in. */
export const DETAILS_FIELD_ORDER: DetailsField[] = [
  'name',
  'description',
  'problemSolved',
  'categoryId',
  'accessId',
  'departmentId',
  'estimatedTimeSaved',
  'dependencies',
  'requirements',
];

/**
 * Validate the Details stage.
 *
 * `requirementsRequired` reflects what only the SERVER can know — whether the selected flow's
 * blueprint actually contains connectors. The caller passes the result of the pre-detect; when it is
 * false (or unknown) the Requirements field stays optional here and the server's
 * `connector_setup_notes_required` 422 remains the real gate. This mirrors the existing Stage 3.5b
 * behaviour rather than inventing a second, client-side policy.
 */
export function validateDetails(
  details: DraftDetails,
  options: { requirementsRequired: boolean },
): DetailsErrors {
  const errors: DetailsErrors = {};

  const name = details.name.trim();
  if (name === '') errors.name = 'Give your tool a name.';
  else if (name.length < LIMITS.nameMin) errors.name = `Use at least ${LIMITS.nameMin} characters.`;
  else if (name.length > LIMITS.nameMax)
    errors.name = `Keep the name to ${LIMITS.nameMax} characters or fewer.`;

  const description = details.description.trim();
  if (description === '') errors.description = 'Say what this tool does.';
  else if (description.length < LIMITS.descriptionMin)
    errors.description = `Use at least ${LIMITS.descriptionMin} characters.`;
  else if (description.length > LIMITS.descriptionMax)
    errors.description = `Keep the description to ${LIMITS.descriptionMax} characters or fewer.`;

  const problem = details.problemSolved.trim();
  if (problem === '') errors.problemSolved = 'Say which problem this solves.';
  else if (problem.length < LIMITS.problemMin)
    errors.problemSolved = `Use at least ${LIMITS.problemMin} characters.`;
  else if (problem.length > LIMITS.problemMax)
    errors.problemSolved = `Keep this to ${LIMITS.problemMax} characters or fewer.`;

  // The three classifications come from governed lists (GC-9). A zero/absent id means the reference
  // data never loaded or the builder cleared the control — either way it cannot be submitted.
  if (!details.categoryId) errors.categoryId = 'Pick a primary category.';
  if (!details.accessId) errors.accessId = 'Set who may use it.';
  if (!details.departmentId) errors.departmentId = 'Say which department it is for.';

  const minutes = Number(details.estimatedTimeSaved);
  if (details.estimatedTimeSaved.trim() === '') {
    errors.estimatedTimeSaved = 'Estimate the time this saves each time it is used.';
  } else if (!Number.isInteger(minutes)) {
    errors.estimatedTimeSaved = 'Enter a whole number of minutes.';
  } else if (minutes < LIMITS.timeSavedMin || minutes > LIMITS.timeSavedMax) {
    errors.estimatedTimeSaved = `Enter between ${LIMITS.timeSavedMin} and ${LIMITS.timeSavedMax} minutes.`;
  }

  // UP-F16: dependencies are mandatory, not optional. "None" is a valid answer — saying so explicitly
  // is the point, so an adopter knows nothing was forgotten.
  if (details.dependencies.trim() === '') {
    errors.dependencies = 'List what this depends on, or write “None”.';
  }

  const requirements = details.requirements.trim();
  if (options.requirementsRequired && requirements === '') {
    errors.requirements = 'This flow uses connectors — explain how to set them up.';
  } else if (requirements !== '' && requirements.length < LIMITS.requirementsMin) {
    errors.requirements = `Use at least ${LIMITS.requirementsMin} characters, or leave it blank.`;
  } else if (requirements.length > LIMITS.requirementsMax) {
    errors.requirements = `Keep this to ${LIMITS.requirementsMax} characters or fewer.`;
  }

  return errors;
}

/** True when Details has no outstanding problems. */
export function detailsValid(errors: DetailsErrors): boolean {
  return Object.keys(errors).length === 0;
}

/**
 * The first invalid field IN PAGE ORDER, or null when Details is valid. This is what the Next handler
 * scrolls and focuses, so the builder is moved to the top-most thing that needs their attention
 * rather than to whichever key the validator happened to write first.
 */
export function firstInvalidField(errors: DetailsErrors): DetailsField | null {
  return DETAILS_FIELD_ORDER.find((field) => errors[field] !== undefined) ?? null;
}
